/**
 * @file src/local/localFileAccess.js
 * @description How a file on this machine is reached — three platforms, one shape.
 *
 * The library stores a `FileRef`-shaped fact about where a file is, and asks this module to turn
 * it back into bytes. Which platform answers is decided here and nowhere else:
 *
 *   desktop  the Electron shell (`window.electron.localAudio`): a real PATH, read with no prompt.
 *            Nothing is copied — the shell reads the file the user actually has.
 *   browser  everywhere else: the file is COPIED once into this browser's own private storage
 *            (`localByteVault.js`) and the library plays its copy. No permission click, no
 *            re-grant, and it works in Firefox, Safari, Brave and on a phone.
 *   memory   a browser with no private file system at all: the dropped `File` is kept for this
 *            session only, and after a reload the entry is missing until the file is dropped again.
 *
 * A `FileSystemFileHandle` is still READ when an entry has one — entries imported before the app
 * kept copies still work exactly as they did — but nothing new is stored that way.
 *
 * ## The desktop contract
 *
 * The shell exposes, on `window.electron.localAudio`:
 *
 *   pathForFile(file: File): string | null                 the OS path of a dropped File
 *   readFile(path: string): Promise<ArrayBuffer>           the bytes, or a rejection when gone
 *   stat(path: string): Promise<{ size: number, mtimeMs: number } | null>
 *   pickFiles(): Promise<Array<{ path, name, size, mtimeMs }>>
 *   pickFolder(): Promise<{ path: string, name: string } | null>
 *   listAudioFiles(folder: string): Promise<Array<{ path, name, size, mtimeMs }>>
 *
 * Nothing here decodes, analyses or stores. It reaches files.
 */
import { LOCAL_KNOWN_UNSUPPORTED_EXTENSIONS, LOCAL_SUPPORTED_EXTENSIONS } from './localAudioConfig.js';
import { hasVaultCopy, readVaultCopy, vaultSupported } from './localByteVault.js';

/** @typedef {{ kind: 'handle'|'path'|'file', name: string, size: number, lastModified: number|null, mimeType: string|null, handle?: any, path?: string|null, file?: File|null, folder?: string|null, folderHandle?: any }} FileRef */

const sessionFiles = new Map(); // entry id → File (memory platform, this session only)

function desktopBridge() {
  try {
    const bridge = typeof window !== 'undefined' ? window.electron?.localAudio : null;
    return bridge && typeof bridge.readFile === 'function' ? bridge : null;
  } catch {
    return null;
  }
}

function hasFileSystemAccess() {
  return typeof window !== 'undefined' && typeof window.showOpenFilePicker === 'function';
}

/** Which platform reaches files here. */
export function localFilePlatform() {
  if (desktopBridge()) return 'desktop';
  if (vaultSupported()) return 'browser';
  return 'memory';
}

/** Whether a file imported here is copied into this browser. False only in the desktop shell. */
export function localFilesAreCopied() {
  return localFilePlatform() === 'browser';
}

/** The extension, lower-cased, or '' when there is none. */
export function fileExtension(name) {
  const match = /\.([a-z0-9]+)$/i.exec(String(name || ''));
  return match ? match[1].toLowerCase() : '';
}

/**
 * Whether a file may enter the library, by name and type — the cheap test. The decode is the
 * real one, and a file that passes here and fails there is reported by the analysis.
 * @returns {'ok'|'unsupported-audio'|'not-audio'}
 */
export function classifyFile({ name, mimeType }) {
  const ext = fileExtension(name);
  if (LOCAL_SUPPORTED_EXTENSIONS.has(ext)) return 'ok';
  if (LOCAL_KNOWN_UNSUPPORTED_EXTENSIONS.has(ext)) return 'unsupported-audio';
  if (typeof mimeType === 'string' && mimeType.startsWith('audio/')) {
    // A MIME we recognise as audio with an extension we do not: let the decoder decide.
    return mimeType === 'audio/aiff' || mimeType === 'audio/x-aiff' ? 'unsupported-audio' : 'ok';
  }
  return 'not-audio';
}

function refFromFile(file, { folder = null, folderHandle = null } = {}) {
  const bridge = desktopBridge();
  const path = bridge && typeof bridge.pathForFile === 'function' ? bridge.pathForFile(file) : null;
  if (path) {
    return { kind: 'path', name: file.name, size: file.size, lastModified: file.lastModified ?? null, mimeType: file.type || null, path, folder: folder ?? folderOfPath(path), file };
  }
  return { kind: 'file', name: file.name, size: file.size, lastModified: file.lastModified ?? null, mimeType: file.type || null, file, folder, folderHandle };
}

async function refFromHandle(handle, { folder = null, folderHandle = null } = {}) {
  const file = await handle.getFile();
  return { kind: 'handle', name: file.name, size: file.size, lastModified: file.lastModified ?? null, mimeType: file.type || null, handle, folder, folderHandle, file };
}

function folderOfPath(path) {
  if (!path) return null;
  const cut = Math.max(path.lastIndexOf('/'), path.lastIndexOf('\\'));
  return cut > 0 ? path.slice(0, cut) : null;
}

async function walkDirectoryHandle(directory, folderName, out, depth = 0) {
  if (depth > 8) return;
  for await (const child of directory.values()) {
    if (child.kind === 'file') {
      out.push(await refFromHandle(child, { folder: folderName, folderHandle: directory }));
    } else if (child.kind === 'directory') {
      await walkDirectoryHandle(child, `${folderName}/${child.name}`, out, depth + 1);
    }
  }
}

async function walkWebkitEntry(entry, folderName, out, depth = 0) {
  if (!entry || depth > 8) return;
  if (entry.isFile) {
    const file = await new Promise((resolve, reject) => entry.file(resolve, reject));
    out.push(refFromFile(file, { folder: folderName }));
    return;
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    const readAll = async () => {
      const batch = await new Promise((resolve, reject) => reader.readEntries(resolve, reject));
      if (!batch.length) return;
      for (const child of batch) await walkWebkitEntry(child, `${folderName}/${child.name}`, out, depth + 1);
      await readAll();
    };
    await readAll();
  }
}

/**
 * Everything a drop carried, folders expanded. Must be called from the drop handler itself: the
 * handle promises have to be taken synchronously, before the event's data transfer is released.
 * @param {DataTransfer} dataTransfer
 * @returns {Promise<FileRef[]>}
 */
export async function collectDroppedFiles(dataTransfer) {
  const items = dataTransfer?.items ? Array.from(dataTransfer.items) : [];
  const out = [];
  const pending = [];
  if (items.length && hasFileSystemAccess() && typeof items[0].getAsFileSystemHandle === 'function' && !desktopBridge()) {
    // Both taken synchronously, before any await: the item list is released when the handler returns.
    const files = [];
    for (const item of items) {
      if (item.kind !== 'file') continue;
      pending.push(item.getAsFileSystemHandle());
      files.push(item.getAsFile());
    }
    const handles = await Promise.all(pending);
    for (let i = 0; i < handles.length; i += 1) {
      const handle = handles[i];
      if (handle?.kind === 'file') out.push(await refFromHandle(handle));
      else if (handle?.kind === 'directory') await walkDirectoryHandle(handle, handle.name, out);
      // A file with no handle behind it (a synthetic drop, or a source the browser will not hand
      // over): playable this session, remembered as missing after a restart.
      else if (files[i]) out.push(refFromFile(files[i]));
    }
    return out;
  }
  // Desktop shell and other browsers: plain Files, with the legacy entry API for folders.
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const entry = typeof item.webkitGetAsEntry === 'function' ? item.webkitGetAsEntry() : null;
    if (entry?.isDirectory) {
      pending.push(walkWebkitEntry(entry, entry.name, out));
      continue;
    }
    const file = item.getAsFile();
    if (file) out.push(refFromFile(file));
  }
  if (!items.length && dataTransfer?.files?.length) {
    for (const file of Array.from(dataTransfer.files)) out.push(refFromFile(file));
  }
  await Promise.all(pending);
  return out;
}

/** The system file picker, multiple files. Resolves [] when the person cancels. */
export async function pickLocalFiles() {
  const bridge = desktopBridge();
  if (bridge && typeof bridge.pickFiles === 'function') {
    const picked = await bridge.pickFiles();
    return (picked || []).map((p) => ({ kind: 'path', name: p.name, size: p.size, lastModified: p.mtimeMs ?? null, mimeType: null, path: p.path, folder: folderOfPath(p.path) }));
  }
  if (hasFileSystemAccess()) {
    try {
      const handles = await window.showOpenFilePicker({
        multiple: true,
        types: [{ description: 'Audio', accept: { 'audio/*': [...LOCAL_SUPPORTED_EXTENSIONS].map((e) => `.${e}`) } }],
      });
      return Promise.all(handles.map((handle) => refFromHandle(handle)));
    } catch (error) {
      if (error?.name === 'AbortError') return [];
      throw error;
    }
  }
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'audio/*';
    input.onchange = () => resolve(Array.from(input.files || []).map((file) => refFromFile(file)));
    input.oncancel = () => resolve([]);
    input.click();
  });
}

/** The system folder picker. Resolves null when cancelled; otherwise the folder and its files. */
export async function pickLocalFolder() {
  const bridge = desktopBridge();
  if (bridge && typeof bridge.pickFolder === 'function') {
    const folder = await bridge.pickFolder();
    if (!folder) return null;
    const files = await bridge.listAudioFiles(folder.path);
    return {
      name: folder.name || folder.path,
      files: (files || []).map((p) => ({ kind: 'path', name: p.name, size: p.size, lastModified: p.mtimeMs ?? null, mimeType: null, path: p.path, folder: folderOfPath(p.path) })),
    };
  }
  if (typeof window !== 'undefined' && typeof window.showDirectoryPicker === 'function') {
    try {
      const directory = await window.showDirectoryPicker({ mode: 'read' });
      const files = [];
      await walkDirectoryHandle(directory, directory.name, files);
      return { name: directory.name, files, handle: directory };
    } catch (error) {
      if (error?.name === 'AbortError') return null;
      throw error;
    }
  }
  return null;
}

/** The fields the store keeps from a ref: how to get back to the file. */
export function persistedLocationOf(ref) {
  return {
    handle: ref.kind === 'handle' ? ref.handle : null,
    folderHandle: ref.folderHandle ?? null,
    path: ref.kind === 'path' ? ref.path : null,
    folder: ref.folder ?? null,
  };
}

/** Keep the dropped File for this session (memory platform, and a cheap first read anywhere). */
export function rememberSessionFile(id, file) {
  if (id && file) sessionFiles.set(id, file);
}

export function forgetSessionFile(id) {
  sessionFiles.delete(id);
}

/**
 * Can the file be reached right now?
 * @returns {Promise<'ready'|'permission'|'missing'>}
 */
export async function checkEntryPresence(entry) {
  if (!entry) return 'missing';
  if (sessionFiles.has(entry.id)) return 'ready';
  // The copy answers before anything else: an entry this browser holds is reachable whatever
  // happened to the file it came from, and must never ask for permission again.
  if (entry.copied && (await hasVaultCopy(entry.id))) return 'ready';
  if (entry.path) {
    const bridge = desktopBridge();
    if (!bridge) return 'missing';
    try {
      const stat = await bridge.stat(entry.path);
      return stat ? 'ready' : 'missing';
    } catch {
      return 'missing';
    }
  }
  // An entry that says it was copied and has none was evicted (Safari clears unpersisted storage).
  // That is the same fact as a file moved on disk, and gets the same state and the same way back.
  if (entry.copied) return 'missing';
  if (entry.handle) {
    try {
      const state = await entry.handle.queryPermission({ mode: 'read' });
      if (state !== 'granted') return 'permission';
      await entry.handle.getFile();
      return 'ready';
    } catch (error) {
      return error?.name === 'NotFoundError' ? 'missing' : 'permission';
    }
  }
  return 'missing';
}

/**
 * Ask the browser to re-open the remembered handles. One user gesture starts the chain; Chrome
 * prompts per handle (a folder handle covers every file under it). Returns the ids re-granted.
 */
export async function requestEntryPermissions(entries) {
  // Every request is ISSUED synchronously, inside the click's user activation — a request made
  // after awaiting an earlier prompt can be refused for want of a gesture. Chrome queues the
  // prompts; a folder handle covers the files under it, so it is asked instead of each of them.
  const asked = new Map();
  for (const entry of entries) {
    const handle = entry.folderHandle ?? entry.handle;
    if (!handle) continue;
    if (!asked.has(handle)) {
      asked.set(handle, Promise.resolve().then(() => handle.requestPermission({ mode: 'read' })).catch((error) => {
        console.warn('[local] permission request failed', entry?.name, error);
        return 'denied';
      }));
    }
  }
  const granted = [];
  for (const entry of entries) {
    const handle = entry.folderHandle ?? entry.handle;
    if (!handle) continue;
    if ((await asked.get(handle)) === 'granted') granted.push(entry.id);
  }
  return granted;
}

/**
 * The bytes of an entry, as a Blob. Throws with `code` 'missing' or 'permission'.
 */
export async function readEntryBytes(entry) {
  if (!entry) throw Object.assign(new Error('No entry.'), { code: 'missing' });
  const session = sessionFiles.get(entry.id);
  if (session) return session;
  if (entry.copied) {
    const copy = await readVaultCopy(entry.id);
    if (copy) return copy;
    // Copied once, gone now. Say so plainly rather than falling through to a handle whose
    // permission lapsed months ago and reporting that instead.
    if (!entry.path) throw Object.assign(new Error('This browser no longer has the file.'), { code: 'missing' });
  }
  if (entry.path) {
    const bridge = desktopBridge();
    if (!bridge) throw Object.assign(new Error('The desktop shell is not here.'), { code: 'missing' });
    try {
      const buffer = await bridge.readFile(entry.path);
      return new Blob([buffer], { type: entry.mimeType || 'application/octet-stream' });
    } catch (error) {
      throw Object.assign(new Error(error?.message || 'File not found.'), { code: 'missing' });
    }
  }
  if (entry.handle) {
    try {
      const state = await entry.handle.queryPermission({ mode: 'read' });
      if (state !== 'granted') {
        const asked = await entry.handle.requestPermission({ mode: 'read' });
        if (asked !== 'granted') throw Object.assign(new Error('Permission declined.'), { code: 'permission' });
      }
      return await entry.handle.getFile();
    } catch (error) {
      if (error?.code) throw error;
      throw Object.assign(new Error(error?.message || 'File not found.'), {
        code: error?.name === 'NotFoundError' ? 'missing' : 'permission',
      });
    }
  }
  throw Object.assign(new Error('This browser cannot reopen the file.'), { code: 'missing' });
}

/**
 * The bytes a freshly-collected ref points at — before there is an entry, and before anything has
 * been copied. This is the one read of the user's actual file; everything afterwards reads the
 * copy the library made from it.
 * @param {FileRef} ref
 * @returns {Promise<Blob>}
 */
export async function readRefBytes(ref) {
  if (!ref) throw Object.assign(new Error('No file.'), { code: 'missing' });
  if (ref.file) return ref.file;
  if (ref.handle) {
    try {
      return await ref.handle.getFile();
    } catch (error) {
      throw Object.assign(new Error(error?.message || 'The file could not be read.'), {
        code: error?.name === 'NotFoundError' ? 'missing' : 'permission',
      });
    }
  }
  if (ref.path) {
    const bridge = desktopBridge();
    if (!bridge) throw Object.assign(new Error('The desktop shell is not here.'), { code: 'missing' });
    try {
      const buffer = await bridge.readFile(ref.path);
      return new Blob([buffer], { type: ref.mimeType || 'application/octet-stream' });
    } catch (error) {
      throw Object.assign(new Error(error?.message || 'File not found.'), { code: 'missing' });
    }
  }
  throw Object.assign(new Error('There is no way to read this file.'), { code: 'missing' });
}

/**
 * True when `ref` is the same file the entry remembers: name, size, and — when both sides know it —
 * the modification time. A re-rendered take with the same name and byte count is a different file,
 * and its old waveform and gain must not be kept for it.
 */
export function refMatchesEntry(ref, entry) {
  if (!ref || !entry) return false;
  if (ref.name !== entry.name || Number(ref.size) !== Number(entry.size)) return false;
  const a = Number(ref.lastModified);
  const b = Number(entry.lastModified);
  if (Number.isFinite(a) && a > 0 && Number.isFinite(b) && b > 0) return a === b;
  return true;
}

export function resetLocalFileAccessForTests() {
  sessionFiles.clear();
}
